/**
 * Location.
 *
 * One watcher for the whole app, so the camera, the dock ring and the "cats
 * near you" list all read the same fix. The most recent valid fix is kept so a
 * capture can freeze it the instant the shutter fires, rather than waiting for
 * a fresh reading that would describe a different place. (§56)
 */

export interface Fix {
  lat: number
  lng: number
  accuracy: number
  at: number
}

export type LocationState =
  | { status: 'idle' }
  | { status: 'requesting' }
  | { status: 'granted'; fix: Fix }
  | { status: 'denied' }
  | { status: 'unavailable' }
  | { status: 'error'; message: string }

type Listener = (state: LocationState) => void

const STALE_AFTER_MS = 90_000

class LocationService {
  private state: LocationState = { status: 'idle' }
  private listeners = new Set<Listener>()
  private watchId: number | null = null
  private watchers = 0
  private lastFix: Fix | null = null

  get current() {
    return this.state
  }

  /** The last fix we trust, or null if it is too old to describe "here". */
  get freshFix(): Fix | null {
    if (!this.lastFix) return null
    return Date.now() - this.lastFix.at < STALE_AFTER_MS ? this.lastFix : null
  }

  /** Any fix at all, however old — better than nothing when saving offline. */
  get anyFix(): Fix | null {
    return this.lastFix
  }

  subscribe(listener: Listener) {
    this.listeners.add(listener)
    listener(this.state)
    return () => {
      this.listeners.delete(listener)
    }
  }

  private set(state: LocationState) {
    this.state = state
    this.listeners.forEach((l) => l(state))
  }

  /** Have we already been granted permission, so we can start without asking? */
  async permission(): Promise<PermissionState | 'unsupported'> {
    if (!('geolocation' in navigator)) return 'unsupported'
    if (!navigator.permissions?.query) return 'prompt'
    try {
      const result = await navigator.permissions.query({ name: 'geolocation' })
      return result.state
    } catch {
      return 'prompt'
    }
  }

  /**
   * Starts watching. Safe to call from several places: the watcher is shared
   * and stops when the last caller releases it.
   */
  start(): () => void {
    if (!('geolocation' in navigator)) {
      this.set({ status: 'unavailable' })
      return () => {}
    }

    this.watchers++
    if (this.watchId === null) {
      if (this.state.status === 'idle') this.set({ status: 'requesting' })
      this.watchId = navigator.geolocation.watchPosition(
        (position) => {
          const fix: Fix = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy,
            at: position.timestamp,
          }
          this.lastFix = fix
          this.set({ status: 'granted', fix })
        },
        (error) => {
          if (error.code === error.PERMISSION_DENIED) this.set({ status: 'denied' })
          else if (error.code === error.POSITION_UNAVAILABLE) this.set({ status: 'unavailable' })
          else this.set({ status: 'error', message: 'No se ha podido leer la ubicación.' })
        },
        { enableHighAccuracy: true, maximumAge: 15_000, timeout: 20_000 },
      )
    }

    let released = false
    return () => {
      if (released) return
      released = true
      this.watchers--
      if (this.watchers <= 0 && this.watchId !== null) {
        navigator.geolocation.clearWatch(this.watchId)
        this.watchId = null
        this.watchers = 0
      }
    }
  }

  /** A single reading, for when a screen needs one fix and not a stream. */
  once(timeoutMs = 12_000): Promise<Fix> {
    return new Promise((resolve, reject) => {
      if (!('geolocation' in navigator)) {
        reject(new Error('unavailable'))
        return
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const fix: Fix = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy,
            at: position.timestamp,
          }
          this.lastFix = fix
          this.set({ status: 'granted', fix })
          resolve(fix)
        },
        (error) => {
          if (error.code === error.PERMISSION_DENIED) this.set({ status: 'denied' })
          reject(new Error(String(error.code)))
        },
        { enableHighAccuracy: true, maximumAge: 10_000, timeout: timeoutMs },
      )
    })
  }
}

export const locationService = new LocationService()

/**
 * How much of the accuracy ring is filled. A 10 m fix is as good as it gets on
 * a phone; anything past 200 m is barely worth drawing.
 */
export function accuracyFraction(accuracy: number | null | undefined): number {
  if (accuracy == null || !Number.isFinite(accuracy)) return 0
  if (accuracy <= 10) return 1
  if (accuracy >= 200) return 0.08
  return Math.max(0.08, Math.min(1, 1 - (accuracy - 10) / 190))
}
